import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import { ClausesComponentVM } from 'ent-components-platform-react';
import metadata from './Offerings.metadata.json5';
import styles from './Offerings.module.scss'

function Offerings(props) {
    const { data: offeringVM, path: coveragePath, onValueChange,
        submissionVM, onValueChangeAndSync, index, isCovLoading,
        showError, updateWizardData, isComponentValid,
        setCurrentOfferingVM, setCurrentIndex, setCoverageValidation,
        isCoverageSummaryScreen, showAdditionalFieldError } = props;

    useEffect(() => {
        if (!isCoverageSummaryScreen) {
            setCurrentOfferingVM(() => offeringVM);
            setCurrentIndex(() => index);

            const additionalFields = _.get(submissionVM, `lobData.homeowners.offerings.children[${index}].coverages.coveragesAdditionalFields_TWIA.value`);

            if (!additionalFields || (!additionalFields?.actualCashValue_TWIA &&
                !additionalFields?.replacementCashValue_TWIA &&
                !additionalFields?.actualCashValuePersonal_TWIA &&
                !additionalFields?.replacementCashValuePersonal_TWIA)) {
                _.set(submissionVM, `lobData.homeowners.offerings.children[${index}].coverages.coveragesAdditionalFields_TWIA`, {});
            }

            const baseCoverages = offeringVM.coverages.baseCoverages.value;

            if (baseCoverages.find(x => x.publicID === 'TWRD_DW_Personal_Property_Cov') &&
                baseCoverages.find(x => x.publicID === 'TWRD_DW_Personal_Property_Cov').selected) {
                offeringVM.coverages.coveragesAdditionalFields_TWIA.isPersonalPropertySelected_TWIA
                    .value = true;
            } else {
                offeringVM.coverages.coveragesAdditionalFields_TWIA.isPersonalPropertySelected_TWIA
                    .value = false;
            }

            offeringVM.coverages.value.coveragesAdditionalFields_TWIA
                .hoPolicyType_TWIA = submissionVM.lobData.homeowners.policyType_TWIA.value.code;
            offeringVM.coverages.value.coveragesAdditionalFields_TWIA
                .structureType_TWIA = submissionVM.lobData.homeowners.coverables.yourHome
                    .structureType_TWIA.value.code;
            updateWizardData(submissionVM);
        }
    }, []);

    const handleValueChange = useCallback((newValue, path, clause) => {
        onValueChange(newValue, path, clause);
    }, [onValueChange])

    const handleValueChangeAndSync = useCallback((newValue, path, clause) => {
        onValueChangeAndSync(newValue, path, clause, offeringVM, index);
    }, [index, offeringVM, onValueChangeAndSync])

    const renderValueField = useCallback((rowData, rowIndex) => (
        <CurrencyField
            id={`creditAmount_${rowIndex}`}
            value={rowData.amount}
            readOnly
            hideLabel
        />
    ), []);

    const renderNameField = useCallback((rowData) => <span>{rowData.name}</span>, []);

    // DE18433
    const isPolicyChange = _.get(submissionVM, 'baseData.jobType.value.code') === 'PolicyChange';
    const hasCreditsAndSurcharges = _.get(offeringVM, 'coverages.creditsAndSurchagesTotal_TWIA.value') !== undefined;

    const creditTitleVisible = isCoverageSummaryScreen ? !isPolicyChange : hasCreditsAndSurcharges;

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
        },
        baseCoveragesComponent: {
            value: offeringVM.coverages.baseCoverages.value,
            path: `${coveragePath}.coverages.baseCoverages`,
            submissionVM,
            additionalFieldsPath: `${coveragePath}.coverages.coveragesAdditionalFields_TWIA`,
            // eslint-disable-next-line no-secrets/no-secrets
            disableAdditionalFields: _.get(submissionVM, 'lobData.value.homeowners.coverables.construction.msbEligiblePolicyType_TWIA'),
            loadingClause: isCovLoading,
            onValidate: setCoverageValidation,
            isComponentValid,
            showError,
            showAdditionalFieldError,
            isEditable: !isCoverageSummaryScreen,
            isResidentialSummary : isCoverageSummaryScreen,
            labelPosition : !isCoverageSummaryScreen,
            showAmount:true,
            showCoverageNameAmount : !isCoverageSummaryScreen
        },
        additionalCoveragesComponent: {
            value: offeringVM.coverages.additionalCoverages.value,
            path: `${coveragePath}.coverages.additionalCoverages`,
            loadingClause: isCovLoading,
            onValidate: setCoverageValidation,
            isComponentValid,
            showError,
            showAdditionalFieldError,
            isEditable: !isCoverageSummaryScreen,
            isResidentialSummary : isCoverageSummaryScreen,
            labelPosition : !isCoverageSummaryScreen,
            showAmount:true,
            showCoverageNameAmount : !isCoverageSummaryScreen
        },
        exclusionConditionsComponent: {
            value: offeringVM.coverages.exclusions_TWIA.value,
            path: `${coveragePath}.coverages.exclusions_TWIA`,
            loadingClause: isCovLoading,
            onValidate: setCoverageValidation,
            isComponentValid,
            showError,
            showAdditionalFieldError,
            isEditable: !isCoverageSummaryScreen,
            isResidentialSummary : isCoverageSummaryScreen,
            labelPosition : !isCoverageSummaryScreen,
            showAmount:true,
            showCoverageNameAmount : !isCoverageSummaryScreen
        },
        indirectLossCovComponent: {
            value: offeringVM.coverages.indirectLossCoverages_TWIA.value,
            path: `${coveragePath}.coverages.indirectLossCoverages_TWIA`,
            loadingClause: isCovLoading,
            onValidate: setCoverageValidation,
            isComponentValid,
            showError,
            showAdditionalFieldError,
            isEditable: !isCoverageSummaryScreen,
            isResidentialSummary : isCoverageSummaryScreen,
            labelPosition : !isCoverageSummaryScreen,
            showAmount:true,
            showCoverageNameAmount : !isCoverageSummaryScreen
        },
        creditSurchargesTitle: {
            // DE15705
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.creditsAndSurchages_TWIA.value') !== undefined
        },
        creditAndSurchargesTable: {
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.creditsAndSurchages_TWIA.value') !== undefined,
            data: _.get(offeringVM, 'coverages.creditsAndSurchages_TWIA.value')
        },
        totalBaseCovPremium: {
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.baseCoveragesTotal_TWIA.value') !== undefined,
            value: _.get(offeringVM, 'coverages.baseCoveragesTotal_TWIA.value')
        },
        lineTitle: {
            className: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.baseCoveragesTotal_TWIA.value') !== undefined
                ? styles.spansytleleft : styles.spansytle
        },
        totalAddlCovPremium: {
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.additionalCoveragesTotal_TWIA.value') !== undefined,
            value: _.get(offeringVM, 'coverages.additionalCoveragesTotal_TWIA.value')
        },
        additionalTitle: {
            className: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.additionalCoveragesTotal_TWIA.value') !== undefined
                ? styles.spansytleleft : styles.spansytle
        },
        totalIndirectCovPremium: {
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.indirectLossCoveragesTotal_TWIA.value') !== undefined,
            value: _.get(offeringVM, 'coverages.indirectLossCoveragesTotal_TWIA.value')
        },
        indirectLossTitle: {
            className: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.indirectLossCoveragesTotal_TWIA.value') !== undefined
                ? styles.spansytleleft : styles.spansytle
        },
        totalCreditCovPremium: {
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.creditsAndSurchagesTotal_TWIA.value') !== undefined,
            value: _.get(offeringVM, 'coverages.creditsAndSurchagesTotal_TWIA.value')
        },
        creditTitle: {
            visible: creditTitleVisible, // DE18433
            className: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.creditsAndSurchagesTotal_TWIA.value') !== undefined
                ? styles.spansytleleft : styles.spansytle
        },
        totalExclCovPremium: {
            visible: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.exclusionsTotal_TWIA.value') !== undefined,
            value: _.get(offeringVM, 'coverages.exclusionsTotal_TWIA.value')
        },
        exclusionTitle: {
            className: !showAdditionalFieldError &&
                _.get(offeringVM, 'coverages.exclusionsTotal_TWIA.value') !== undefined
                ? styles.spansytleleft : styles.spansytle
        },
        indirectLossCoveragesTitle: {
            visible: _.get(offeringVM, 'coverages.indirectLossCoverages_TWIA.value')?.length > 0
        },
        premium: { 
            visible: !!isCoverageSummaryScreen // DE15834
            
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            clausesComponentVM: ClausesComponentVM,
        },
        resolveCallbackMap: {
            handleValueChange,
            handleValueChangeAndSync,
            renderCreditName: renderNameField,
            renderCreditValue: renderValueField
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={offeringVM}
            overrideProps={overrideProps}
            showErrors={showError || showAdditionalFieldError}
            // onValidationChange={onValidate}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

Offerings.propTypes = {
    data: PropTypes.shape({}).isRequired,
};

export default Offerings;
